import { roundAggregateSchema } from "@engine/round/aggregate";
import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex } from "@noble/hashes/utils.js";
import { type z } from "zod";

/**
 * Bump this when round aggregate output intentionally changes without changing
 * the Zod aggregate shape. This invalidates cached snapshots and generated test
 * data in the same way as a structural schema change.
 */
export const ROUND_AGGREGATE_SCHEMA_HASH_VERSION = 2;

/**
 * Deterministic, dependency-light serialization format for Zod schemas.
 *
 * The goal is to build a stable structural fingerprint that:
 * - does not depend on object property insertion order,
 * - treats equivalent schema declarations as equal regardless of option order
 *   (for example union branches and enum values),
 * - and produces a stable JSON representation for hashing.
 *
 * This keeps the generated hash focused on schema semantics rather than
 * runtime construction details.
 */
type CanonicalSchema =
  | string
  | number
  | boolean
  | null
  | CanonicalRecord
  | CanonicalSchema[]
  | undefined;
type CanonicalRecord = { [key: string]: CanonicalSchema };

/** Normalize an arbitrary JS value for stable JSON serialization. */
function canonicalizeForJson(value: unknown): CanonicalSchema {
  if (value === null || typeof value !== "object") {
    if (typeof value === "function" || typeof value === "symbol") {
      return null;
    }
    if (typeof value === "bigint") {
      return value.toString();
    }
    return value as CanonicalSchema;
  }

  if (Array.isArray(value)) {
    return value.map((item) => canonicalizeForJson(item));
  }

  const record = value as Record<string, unknown>;
  const entries = Object.keys(record)
    .sort()
    .map((key) => [key, canonicalizeForJson(record[key])]);
  return Object.fromEntries(entries);
}

function hashString(value: CanonicalSchema): string {
  const encoded = new TextEncoder().encode(JSON.stringify(value));
  return bytesToHex(sha256(encoded));
}

/** Resolve a Zod default value for hashing across zod v3/v4 internals. */
function resolveDefaultValue(def: Record<string, unknown>): unknown {
  const descriptor = Object.getOwnPropertyDescriptor(def, "defaultValue");
  if (descriptor?.get != null) {
    return descriptor.get.call(def);
  }

  const defaultValue = (def as { defaultValue?: unknown }).defaultValue;
  if (typeof defaultValue === "function") {
    return (defaultValue as () => unknown)();
  }

  return defaultValue;
}

/**
 * Convert a Zod schema into a deterministic structure that captures schema
 * shape and validation semantics while avoiding runtime-unstable internals.
 */
function canonicalizeSchema(schema: z.ZodTypeAny): CanonicalSchema {
  const def =
    (schema as unknown as { _def?: Record<string, unknown> })._def ?? {};
  const typeName =
    (def as { typeName?: string; type?: string }).typeName ??
    (def as { typeName?: string; type?: string }).type ??
    "ZodUnknown";
  const defType =
    typeof (def as { type?: string }).type === "string"
      ? (def as { type?: string }).type
      : undefined;
  const catchall = (def.catchall as z.ZodTypeAny | undefined) ?? null;
  const catchallTypeName =
    (catchall as { _def?: { typeName?: string; type?: string } } | null)?._def
      ?.typeName ??
    (catchall as { _def?: { typeName?: string; type?: string } } | null)?._def
      ?.type;

  switch (typeName) {
    case "ZodObject":
    case "object": {
      const shapeRaw = (def.shape ?? def.shapeObj ?? def._shape ?? null) as
        | Record<string, z.ZodTypeAny>
        | (() => Record<string, z.ZodTypeAny>)
        | null;
      const shape =
        typeof shapeRaw === "function" ? shapeRaw() : (shapeRaw ?? {});
      const sortedShapeKeys = Object.keys(shape).sort();
      const properties = Object.fromEntries(
        sortedShapeKeys.map((key) => {
          const propertySchema = shape[key];
          if (propertySchema == null) {
            throw new Error(`Missing schema for key: ${key}`);
          }
          return [key, canonicalizeSchema(propertySchema)] as const;
        }),
      );
      const required = sortedShapeKeys.filter((key) => {
        const propertySchema = shape[key];
        if (propertySchema == null) {
          return false;
        }
        return !propertySchema.isOptional();
      });
      return {
        kind: "object",
        unknownKeys: canonicalizeForJson(def.unknownKeys),
        catchall:
          catchall !== null &&
          catchallTypeName !== "ZodNever" &&
          catchallTypeName !== "never"
            ? canonicalizeSchema(catchall)
            : null,
        required,
        properties,
      };
    }
    case "ZodArray":
    case "array":
      return {
        kind: "array",
        items: canonicalizeSchema(
          ((def.element as z.ZodTypeAny) ??
            (def.type as z.ZodTypeAny)) as z.ZodTypeAny,
        ),
      };
    case "ZodString":
    case "string":
      return {
        kind: "string",
        checks: canonicalizeChecks((def.checks ?? []) as unknown[]),
      };
    case "ZodNumber":
    case "number":
      return {
        kind: "number",
        checks: canonicalizeChecks((def.checks ?? []) as unknown[]),
        coerce: Boolean(def.coerce),
      };
    case "ZodBoolean":
    case "boolean":
      return { kind: "boolean" };
    case "ZodAny":
    case "any":
      return { kind: "any" };
    case "ZodUnknown":
    case "unknown":
      return { kind: "unknown" };
    case "ZodUndefined":
    case "undefined":
      return { kind: "undefined" };
    case "ZodNull":
    case "null":
      return { kind: "null" };
    case "ZodNever":
    case "never":
      return { kind: "never" };
    case "ZodLiteral":
    case "literal":
      return {
        kind: "literal",
        value: canonicalizeForJson(
          (def.values as unknown[] | undefined)?.[0] ?? def.value,
        ),
      };
    case "ZodEnum":
    case "enum": {
      const values = Array.isArray(def.values)
        ? (def.values as string[])
        : (Object.values(
            (def.entries as Record<string, unknown>) ?? {},
          ) as string[]);
      return {
        kind: "enum",
        values: [...values].sort(),
      };
    }
    case "ZodDiscriminatedUnion":
      return {
        kind: "discriminatedUnion",
        discriminator: def.discriminator as string,
        options: canonicalizeSchemaArraySorted(def.options as z.ZodTypeAny[]),
      };
    case "ZodUnion":
    case "union":
      if ((def.discriminator as string | undefined) != null) {
        return {
          kind: "discriminatedUnion",
          discriminator: def.discriminator as string,
          options: canonicalizeSchemaArraySorted(def.options as z.ZodTypeAny[]),
        };
      }
      return {
        kind: "union",
        options: canonicalizeSchemaArraySorted(def.options as z.ZodTypeAny[]),
      };
    case "ZodIntersection":
    case "intersection":
      return {
        kind: "intersection",
        left: canonicalizeSchema(def.left as z.ZodTypeAny),
        right: canonicalizeSchema(def.right as z.ZodTypeAny),
      };
    case "ZodRecord":
    case "record":
      return {
        kind: "record",
        keyType: canonicalizeSchema(def.keyType as z.ZodTypeAny),
        valueType: canonicalizeSchema(def.valueType as z.ZodTypeAny),
      };
    case "ZodOptional":
    case "optional":
      return {
        kind: "optional",
        schema: canonicalizeSchema(def.innerType as z.ZodTypeAny),
      };
    case "ZodNullable":
    case "nullable":
      return {
        kind: "nullable",
        schema: canonicalizeSchema(def.innerType as z.ZodTypeAny),
      };
    case "ZodDefault":
    case "default":
      return {
        kind: "default",
        schema: canonicalizeSchema(def.innerType as z.ZodTypeAny),
        defaultValue: canonicalizeForJson(resolveDefaultValue(def)),
      };
    case "ZodPipe":
    case "pipe":
    case "ZodEffects":
      return {
        kind: "effects",
        effectType: defType,
        schema: canonicalizeSchema(
          ((def.in as z.ZodTypeAny) ??
            (def.schema as z.ZodTypeAny) ??
            (def.out as z.ZodTypeAny) ??
            (def.effect as z.ZodTypeAny)) as z.ZodTypeAny,
        ),
      };
    default:
      return {
        kind: typeName,
        meta: canonicalizeForJson({
          ...def,
          checks: undefined,
          typeName: undefined,
        }),
      };
  }
}

function canonicalizeChecks(checks: unknown[]) {
  const sortedChecks = checks.map((check) => canonicalizeForJson(check));
  return stableSerializeSortedArray(sortedChecks);
}

function canonicalizeSchemaArraySorted(
  items: z.ZodTypeAny[],
): CanonicalSchema[] {
  const canonicalItems = items.map((item) => canonicalizeSchema(item));
  return stableSerializeSortedArray(canonicalItems);
}

function stableSerializeSortedArray(
  items: CanonicalSchema[],
): CanonicalSchema[] {
  return [...items]
    .sort((left, right) => {
      const leftString = JSON.stringify(canonicalizeForJson(left));
      const rightString = JSON.stringify(canonicalizeForJson(right));
      return leftString.localeCompare(rightString);
    })
    .map((item) => canonicalizeForJson(item));
}

/**
 * Compute a stable SHA-256 hash for an arbitrary Zod schema.
 *
 * This is intentionally generic so we can test hash behavior on representative
 * schema fragments without coupling every test to the full round schema.
 */
export function computeSchemaHash(schema: z.ZodTypeAny): string {
  const canonicalSchema = canonicalizeForJson({
    kind: "zodSchema",
    schema: canonicalizeSchema(schema),
  });
  return hashString(canonicalSchema);
}

/**
 * Compute the compatibility hash for the full round aggregate.
 * This should be used for naming cached test data artifacts and aggregate
 * snapshots, so structural schema changes and deliberate output-semantic
 * changes yield a different name.
 */
export function computeRoundAggregateSchemaHash(): string {
  const canonicalSchema = canonicalizeForJson({
    kind: "roundAggregateSchema",
    version: ROUND_AGGREGATE_SCHEMA_HASH_VERSION,
    schema: canonicalizeSchema(roundAggregateSchema),
  });
  return hashString(canonicalSchema);
}

/**
 * The compatibility hash currently used to read/write aggregate snapshots.
 * This is a stable, deterministic value derived from roundAggregateSchema and
 * ROUND_AGGREGATE_SCHEMA_HASH_VERSION.
 */
export const roundAggregateSchemaHash = computeRoundAggregateSchemaHash();
